import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('auth-token');
    if (!token) return;

    const serverUrl =
      import.meta.env.VITE_API_BACKEND_SOCKET_BASE_URL || 'http://localhost:3000';

    // Force WebSocket-only transport. Behind CloudFront + ALB with multiple
    // backend tasks, socket.io's default HTTP long-polling transport breaks:
    // the polling session is pinned to one task but follow-up requests are not
    // sticky, so every connection fails with "xhr poll error" and realtime never
    // works (the onboarding AI-summary page wedges, matches don't push, etc.).
    // A single WebSocket connection has no such problem and is confirmed to
    // upgrade cleanly through CloudFront (101 Switching Protocols). Cross-task
    // delivery is handled by the backend's socket.io Redis adapter.
    const newSocket = io(`${serverUrl}/ws`, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => setIsConnected(true));
    newSocket.on('disconnect', () => setIsConnected(false));
    newSocket.on('connect_error', error => {
      console.error('WebSocket connection error:', error);
    });

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
